import { GlobalParticipantType, NamespaceDetail, Participant, ParticipantType, SubjectParticipant } from '@vendasta/conversation';
import { CrmObject, FieldValue, PhoneFieldsInterface } from '@vendasta/crm';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';
import { INBOX_CRM_SOURCE_NAME, STANDARD_CRM_FIELD_EXTERNAL_IDS } from './inbox.constants';
import { ConversationAvailableChannels, ConversationDetail } from './interface/conversation.interface';
import { InboxContact } from './interface/inbox.interface';

const DEFAULT_COUNTRY_CODE: CountryCode = 'US';

const PARTICIPANT_TYPE_TO_SNAKE_CASE: Record<GlobalParticipantType, string> = {
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_UNDEFINED]: 'undefined',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP]: 'account_group',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER]: 'partner',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_VENDOR]: 'vendor',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_CUSTOMER]: 'customer',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_IAM_USER]: 'iam_user',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_GROUP]: 'group',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_AI_ASSISTANT]: 'ai_assistant',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ANONYMOUS]: 'anonymous',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_DIGITAL_AGENT]: 'digital_agent',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_OPENAI_BOT]: 'openai_bot',
  [GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_IAM_TEMPORARY_SUBJECT]: 'iam_temporary_subject',
};

export function getParticipantTypeStr(type: GlobalParticipantType): string {
  return PARTICIPANT_TYPE_TO_SNAKE_CASE[type];
}

export interface Namespace {
  id: string;
  type: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP | GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER | GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_VENDOR;
}

export function getSubjectParticipantByType(participants: Participant[], type: ParticipantType): Participant | null {
  return (
    participants?.find((participant) => participant?.isSubjectParticipant && participant?.participantType === type) ??
    null
  );
}

/**
 * isSameParticipant returns true if the target participant is the same based on the fields that makes a participant unique
 * @param userId
 * @param partnerId
 * @param accountGroupId
 * @param {Participant} targetParticipant
 * @returns {boolean}
 */
export function isSameParticipant(
  userId: string,
  partnerId: string,
  accountGroupId: string,
  targetParticipant: Participant,
): boolean {
  return (
    targetParticipant?.internalParticipantId === userId &&
    targetParticipant?.partnerId === partnerId &&
    targetParticipant?.accountGroupId === accountGroupId
  );
}

/** Create a map of contact fields, using fieldID as key
 * @param {CrmObject} customerFields - contactFields
 * @return {Map<string, FieldValue>} - Map of contact fields
 */
export function createContactFieldMap(contactFields: CrmObject): Map<string, FieldValue> {
  const contactFieldMap = new Map<string, FieldValue>();
  contactFields.fields.forEach((field) => {
    if (field?.fieldId) contactFieldMap.set(field.externalId, field);
  });
  return contactFieldMap;
}

/** Return an InboxContact object from CrmObject
 * @param {string} accountGroupId - accountGroupId
 * @param {CrmObject} contactFields - accountGroupId
 * @return {InboxContact} - Inbox Contact object
 */
export function getInboxContactFromCrm(accountGroupId: string, contactFields: CrmObject): InboxContact {
  const contactFieldMap = createContactFieldMap(contactFields);
  const inboxContact: InboxContact = {
    contactId: contactFields.crmObjectId,
    firstName: contactFieldMap.get(STANDARD_CRM_FIELD_EXTERNAL_IDS.firstName)?.stringValue ?? '',
    lastName: contactFieldMap.get(STANDARD_CRM_FIELD_EXTERNAL_IDS.lastName)?.stringValue ?? '',
    email: contactFieldMap.get(STANDARD_CRM_FIELD_EXTERNAL_IDS.email)?.stringValue ?? '',
    phone: contactFieldMap.get(STANDARD_CRM_FIELD_EXTERNAL_IDS.phoneNumber)?.stringValue ?? '',
    permissionToContact: true, //TODO (MEGA-221): hydrate this field when CRM service support it or delete it if not needed
    accountGroupId: accountGroupId,
  };
  return inboxContact;
}

export function createCrmField(fieldName: string, stringValue: string): FieldValue | null {
  if (!fieldName || !stringValue) return null;
  return {
    externalId: fieldName,
    stringValue: stringValue,
  } as FieldValue;
}

export function buildCrmFieldsForCreateContact(inboxContact: InboxContact): FieldValue[] {
  let parsedPhoneVals: PhoneFieldsInterface | null = null;

  if (inboxContact?.phone) {
    try {
      const parsedNumber = parsePhoneNumberFromString(inboxContact.phone, DEFAULT_COUNTRY_CODE);
      
      if (!parsedNumber) {
        console.warn('Failed to parse phone number:', inboxContact.phone);
      } else {
        parsedPhoneVals = {
          isoCountryCode: parsedNumber.country || DEFAULT_COUNTRY_CODE,
          nationalNumber: parsedNumber.nationalNumber,
          extension: parsedNumber.ext || '',
        };
      }
    } catch (error) {
      console.error('Failed to parse phone number:', error);
    }
  }

  const object = [
    createCrmField(STANDARD_CRM_FIELD_EXTERNAL_IDS.firstName, inboxContact?.firstName ?? ''),
    createCrmField(STANDARD_CRM_FIELD_EXTERNAL_IDS.lastName, inboxContact?.lastName ?? ''),
    createCrmFieldPhoneValue(STANDARD_CRM_FIELD_EXTERNAL_IDS.phoneNumber, parsedPhoneVals),
    createCrmField(STANDARD_CRM_FIELD_EXTERNAL_IDS.email, inboxContact?.email ?? ''),
    createCrmField(STANDARD_CRM_FIELD_EXTERNAL_IDS.source, INBOX_CRM_SOURCE_NAME),
  ].filter((field) => !!field);

  return object;
}

export function createCrmFieldPhoneValue(
  fieldName: string,
  phoneFieldValues?: PhoneFieldsInterface | null,
): FieldValue | null {
  if (!fieldName || !phoneFieldValues) return null;
  return {
    externalId: fieldName,
    phoneFieldValues: phoneFieldValues,
  } as FieldValue;
}

/**
 * Checks if the contact information is missing
 * @param {ConversationDetail} conversationDetail - Conversation Detail information
 * @param {ConversationAvailableChannels} availableChannels - Conversation Available Channels
 * @return {boolean}
 */
export function isContactInformationMissing(
  conversationDetail: ConversationDetail,
  availableChannels: ConversationAvailableChannels,
): boolean {
  if (!conversationDetail?.conversation) return false;

  const recipients = conversationDetail?.participants
    ?.filter((p) => p.isSubjectParticipant && p.internalParticipantId)
    .filter((participant) => participant.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER);

  if (recipients?.length === 0) return false;

  const contactParticipant = conversationDetail?.participants?.find(
    (participant) => participant?.internalParticipantId === recipients[0].internalParticipantId,
  );

  return (
    !contactParticipant?.phoneNumber && !contactParticipant?.email && !availableChannels?.availableChannels?.length
  );
}

/**
 * Checks if the NamespaceDetail information used for describe participant hierarchy contains a participantType
 * @param {NamespaceDetail[]} hierarchy - Participant Hierarchy
 * @param {GlobalParticipantType} participantType - Participant Type
 * @return {boolean}
 */
export function isSubjectParticipantInHierarchy(
  hierarchy: NamespaceDetail[],
  participantType: GlobalParticipantType,
): boolean {
  if (!hierarchy || !hierarchy.length) return false;
  return hierarchy.some((namespace) => namespace.participantType === participantType);
}

/**
 * Computes the subject participant pair key for two participants.
 * The pair key is formatted as "type_snake:id|type_snake:id" (lexically ordered).
 * Used for querying conversations between specific participant pairs.
 *
 * @param participant1 - First subject participant
 * @param participant2 - Second subject participant
 * @returns The computed pair key string
 */
export function computeSubjectParticipantPairKey(
  participant1: { type: GlobalParticipantType; internalId: string },
  participant2: { type: GlobalParticipantType; internalId: string },
): string {
  const key1 = `${PARTICIPANT_TYPE_TO_SNAKE_CASE[participant1.type]}:${participant1.internalId}`;
  const key2 = `${PARTICIPANT_TYPE_TO_SNAKE_CASE[participant2.type]}:${participant2.internalId}`;

  return key1 < key2 ? `${key1}|${key2}` : `${key2}|${key1}`;
}

/**
 * Computes all subject participant pair keys for a list of subject participants.
 * Generates all unique 2-combinations with each pair formatted as "type_snake:id|type_snake:id" (lexically ordered).
 * Duplicates are removed before computing combinations.
 *
 * @param subjectParticipants - Array of subject participants
 * @returns Sorted array of pair key strings
 */
export function computeSubjectParticipantPairKeys(subjectParticipants: SubjectParticipant[]): string[] {
  if (!subjectParticipants || subjectParticipants.length < 2) {
    return [];
  }

  const uniqueParticipants = Array.from(
    new Map(
      subjectParticipants.map((p) => [
        `${p.participantType}:${p.internalParticipantId}`,
        { type: p.participantType, internalId: p.internalParticipantId },
      ]),
    ).values(),
  );

  if (uniqueParticipants.length < 2) {
    return [];
  }

  const pairKeys: string[] = [];
  for (let i = 0; i < uniqueParticipants.length; i++) {
    for (let j = i + 1; j < uniqueParticipants.length; j++) {
      pairKeys.push(computeSubjectParticipantPairKey(uniqueParticipants[i], uniqueParticipants[j]));
    }
  }

  return pairKeys.sort();
}
